import type { TSelectEvaluationSummary } from 'types/evaluation.types';
import {
  EEvaluationType,
  type TSelectionCountCriteria,
  hasNestedKeys,
  type TMultipleSelectOption,
  type TMultipleSelectQuestion,
  type TAvailableLanguageCodes,
} from '@hubertai/screening-question-types';

function maxSumFromSelections(values: number[], maxSelections: number): number {
  if (maxSelections <= 0 || values.length === 0) {
    return 0;
  }

  return values
    .slice() // create a copy so we don’t mutate the original
    .sort((a, b) => b - a) // sort descending
    .slice(0, maxSelections) // pick top N values
    .reduce((sum, val) => sum + val, 0); // sum them
}

const evaluateMultipleSelect = (
  question: TMultipleSelectQuestion,
  _language: TAvailableLanguageCodes,
  nodeEvaluation: any,
) => {
  const temp: TSelectEvaluationSummary = {
    id: question.id,
    evaluation: {},
    summary: {
      [EEvaluationType.Bonus]: 0,
      [EEvaluationType.Warning]: 0,
      [EEvaluationType.Disqualified]: 0,
      [EEvaluationType.Info]: 0,
    },
    isDefault: true,
  };

  /**
   * This must be the same in the backend!!
   * See class SingleSelectEvaluation
   * @param Evaluation  template.evaluation
   * @param option      option
   * @param x           TSelectEvaluationSummary
   */
  function summarizeMultipleSelectOptionEvaluation(
    nodeEvaluation: any,
    option: TMultipleSelectOption,
    x: TSelectEvaluationSummary,
  ) {
    const key = `${option.id}`;

    // A template override wins outright when present; otherwise fall back to the question's own
    // default (evaluation/bonus_points live directly on the option, not nested) — same precedence
    // getSelectOptionEvaluation uses on the backend. Falling back to the default isn't a template
    // customization, so it doesn't flip isDefault the way a real override does.
    const hasOverride = hasNestedKeys(nodeEvaluation, option.id, 'evaluation');
    let evaluation;

    if (hasOverride) {
      evaluation = nodeEvaluation[option.id].evaluation;
    } else if (option.evaluation !== undefined) {
      evaluation = {
        name: option.evaluation,
        bonus_points: option.bonus_points,
      };
    }

    if (evaluation) {
      if (evaluation.name === EEvaluationType.Info) {
        x.summary[EEvaluationType.Info] += 1;
      }

      if (evaluation.name === EEvaluationType.Warning) {
        x.summary[EEvaluationType.Warning] += 1;
      }

      if (evaluation.name === EEvaluationType.Bonus) {
        x.summary[EEvaluationType.Bonus] += evaluation.bonus_points ?? 0;
      }

      if (evaluation.name === EEvaluationType.Disqualified) {
        x.summary[EEvaluationType.Disqualified] += 1;
      }

      if (!x.evaluation) {
        x.evaluation = {};
      }
      x.evaluation[key] = {
        id: option.id,
        name: evaluation.name,
        isDefault: !hasOverride,
      };

      if (evaluation.name === EEvaluationType.Bonus) {
        x.evaluation[key]['bonus_points'] = evaluation.bonus_points;
      }

      if (hasOverride) {
        x.isDefault = false;
      }
    }
  }

  // Only evaluate options that are enabled for the current language
  const options = question.options.filter((o) => _language in o.enabled && o.enabled[_language]);

  options.map((option) => {
    summarizeMultipleSelectOptionEvaluation(nodeEvaluation, option, temp);
  });

  if (question.max_selections && question.max_selections > 0) {
    const bpArray = options.filter((o) => o.evaluation === 'bonus').map((o) => o.bonus_points ?? 0);
    const bpSum = maxSumFromSelections(bpArray, question.max_selections);
    temp.summary[EEvaluationType.Bonus] = bpSum;
  }

  /**
   * Summarize selection count criteria
   * This must be the same in backend!
   * See evaluateMultipleSelect
   * @param option      option
   */
  const summarizeSelectionCountCriteria = (x: TSelectionCountCriteria[], y: TSelectEvaluationSummary) => {
    let bpMax = 0;
    x.forEach((criteria) => {
      const evaluation = criteria.evaluation;
      if (evaluation.name === EEvaluationType.Info) {
        y.summary[EEvaluationType.Info] += 1;
      }
      if (evaluation.name === EEvaluationType.Warning) {
        y.summary[EEvaluationType.Warning] += 1;
      }
      if (evaluation.name === EEvaluationType.Bonus) {
        bpMax = Math.max(bpMax, evaluation.bonus_points ?? 0);
      }
      if (evaluation.name === EEvaluationType.Disqualified) {
        y.summary[EEvaluationType.Disqualified] += 1;
      }
    });

    y.summary[EEvaluationType.Bonus] += bpMax ?? 0;
  };

  const selectionCount = (nodeEvaluation && nodeEvaluation.selection_count) ?? [];
  summarizeSelectionCountCriteria(selectionCount, temp);

  return temp;
};

export default evaluateMultipleSelect;
